import { useState } from 'react';
import { X } from '../../lib/icons';
import { toast } from 'sonner';
import { createCuve, UNITES } from '../../lib/dataProvider';

const field =
  'mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

const QUALITES_NOBLE = ['Extra-Vierge', 'Vierge', 'Vierge (compte client)'];

export const CuveForm = ({ onClose, onCreated }) => {
  const [f, setF] = useState({
    nom: '',
    famille: 'noble',
    qualite: 'Extra-Vierge',
    unite: 'L',
    capacite: '',
    emplacement: '',
    proprietaire: 'huilerie',
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const lamp = f.famille === 'lampante';

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nom.trim()) return toast.error('Saisissez la désignation de la cuve');
    if (!Number(f.capacite)) return toast.error('Saisissez la capacité maximale');
    await createCuve({
      nom: f.nom.trim(),
      famille: f.famille,
      qualite: lamp ? 'Lampante' : f.qualite,
      unite: f.unite,
      capacite: Number(f.capacite),
      emplacement: f.emplacement.trim() || '—',
      proprietaire: f.proprietaire,
    });
    toast.success(`Cuve « ${f.nom.trim()} » créée`);
    onCreated();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="cuve-form-overlay">
      <form onSubmit={submit} data-testid="cuve-form" className="w-full max-w-lg border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">
              Nouvelle cuve / pile
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Configuration avant campagne
            </p>
          </div>
          <button
            type="button"
            data-testid="cuve-form-close"
            onClick={onClose}
            className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label className={label}>Désignation</label>
            <input
              data-testid="cuve-input-nom"
              value={f.nom}
              onChange={set('nom')}
              placeholder="ex. B3, P2, Pile Abdel Aziz"
              className={field}
            />
          </div>

          <div>
            <label className={label}>Filière (verrouillée à la création)</label>
            <div className="mt-2 grid grid-cols-2 gap-px bg-slate-200">
              {[
                { id: 'noble', label: 'Nobles — Vierge / Extra' },
                { id: 'lampante', label: 'Lampante' },
              ].map((o) => {
                const active = f.famille === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`cuve-famille-${o.id}`}
                    onClick={() => setF((p) => ({ ...p, famille: o.id }))}
                    className={`px-3 py-2.5 text-sm font-semibold transition-colors duration-150 ${
                      active
                        ? o.id === 'lampante'
                          ? 'bg-orange-600 text-white'
                          : 'bg-slate-900 text-white'
                        : 'bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 border-l-2 border-slate-900 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              La filière détermine définitivement les affectations autorisées : aucun transfert ne
              sera possible entre une cuve nobles et une cuve lampante.
            </p>
          </div>

          {!lamp && (
            <div>
              <label className={label}>Qualité</label>
              <select data-testid="cuve-select-qualite" value={f.qualite} onChange={set('qualite')} className={field}>
                {QUALITES_NOBLE.map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Capacité maximale</label>
              <input
                data-testid="cuve-input-capacite"
                type="number"
                min="0"
                value={f.capacite}
                onChange={set('capacite')}
                className={field}
              />
            </div>
            <div>
              <label className={label}>Unité</label>
              <select data-testid="cuve-select-unite" value={f.unite} onChange={set('unite')} className={field}>
                {UNITES.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={label}>Emplacement</label>
            <input
              data-testid="cuve-input-emplacement"
              value={f.emplacement}
              onChange={set('emplacement')}
              placeholder="ex. Hall Nord"
              className={field}
            />
          </div>

          <div>
            <label className={label}>Propriété du stock</label>
            <div className="mt-2 grid grid-cols-2 gap-px bg-slate-200">
              {[
                { id: 'huilerie', label: 'Huilerie (stock propre)' },
                { id: 'clients', label: 'Compte clients / agriculteurs' },
              ].map((o) => {
                const active = f.proprietaire === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`cuve-proprietaire-${o.id}`}
                    onClick={() => setF((p) => ({ ...p, proprietaire: o.id }))}
                    className={`px-3 py-2.5 text-sm font-semibold transition-colors duration-150 ${
                      active ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="submit"
            data-testid="cuve-form-submit"
            className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            Créer la cuve
          </button>
          <button
            type="button"
            data-testid="cuve-form-cancel"
            onClick={onClose}
            className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
